import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  SquaresFour, Buildings, UsersThree, Tag, CreditCard, Money, Megaphone,
  GearSix, ClockCounterClockwise, SignOut, List, Crown,
} from "@phosphor-icons/react";
import SADashboard from "./SADashboard";
import SAEmpresas from "./SAEmpresas";
import SAPlanes from "./SAPlanes";
import SADescuentos from "./SADescuentos";
import SASuscripciones from "./SASuscripciones";
import SAPagos from "./SAPagos";
import SAMensajes from "./SAMensajes";
import SAConfig from "./SAConfig";
import SAAuditoria from "./SAAuditoria";

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: SquaresFour },
  { key: "empresas", label: "Empresas", icon: Buildings },
  { key: "usuarios", label: "Usuarios", icon: UsersThree, disabled: true },
  { key: "planes", label: "Planes y precios", icon: Tag },
  { key: "suscripciones", label: "Suscripciones", icon: CreditCard },
  { key: "pagos", label: "Pagos", icon: Money },
  { key: "descuentos", label: "Descuentos", icon: Tag },
  { key: "mensajes", label: "Mensajes y anuncios", icon: Megaphone },
  { key: "config", label: "Configuración del sistema", icon: GearSix },
  { key: "auditoria", label: "Historial de cambios", icon: ClockCounterClockwise },
];

const VIEWS = {
  dashboard: SADashboard,
  empresas: SAEmpresas,
  planes: SAPlanes,
  descuentos: SADescuentos,
  suscripciones: SASuscripciones,
  pagos: SAPagos,
  mensajes: SAMensajes,
  config: SAConfig,
  auditoria: SAAuditoria,
};

function SideNav({ section, setSection, onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5 px-3" data-testid="sa-nav">
      {SECTIONS.map(({ key, label, icon: Icon, disabled }) => (
        <button
          key={key}
          disabled={disabled}
          onClick={() => { setSection(key); onNavigate?.(); }}
          data-testid={`sa-nav-${key}`}
          className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border-l-2 transition-colors duration-200 text-left ${
            disabled
              ? "border-transparent text-muted-foreground/40 cursor-not-allowed"
              : section === key
                ? "border-primary bg-primary/10 text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60"
          }`}
        >
          <Icon size={18} className="shrink-0" />
          {label}
          {disabled && <span className="ml-auto text-[10px] uppercase tracking-wider">pronto</span>}
        </button>
      ))}
    </nav>
  );
}

export default function AdminLayout({ onLogout }) {
  const [section, setSection] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const View = VIEWS[section] || SADashboard;

  const side = (onNav) => (
    <div className="flex h-full flex-col bg-background">
      <div className="px-5 pt-6 pb-5 border-b border-border">
        <div className="font-heading text-lg font-black tracking-tight text-foreground flex items-center gap-2" data-testid="sa-brand">
          <Crown size={18} className="text-primary" /> CAUS · Plataforma
        </div>
        <p className="text-xs text-muted-foreground mt-1">Superadministrador</p>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <SideNav section={section} setSection={setSection} onNavigate={onNav} />
      </div>
      <div className="border-t border-border p-4">
        <Button variant="outline" size="sm" className="w-full rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="sa-logout" onClick={onLogout}>
          <SignOut className="mr-2" size={15} /> Cerrar sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border">
        {side(null)}
      </aside>
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <span className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
          <Crown size={16} className="text-primary" /> CAUS · Plataforma
        </span>
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="rounded-sm border-input bg-transparent" data-testid="sa-mobile-menu">
              <List size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-r border-border bg-background p-0">
            {side(() => setMenuOpen(false))}
          </SheetContent>
        </Sheet>
      </div>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] p-4 sm:p-8">
          <View />
        </div>
      </main>
    </div>
  );
}
